<script lang="ts">
import { defineComponent, PropType, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { $vfm } from 'vue-final-modal';
import { useLocalizedNamesStore } from '../../../stores/localizedNames';
import type { RosterType } from '@interfaces/roster_type';
import type { SpellType } from '@interfaces/spell_type';
import type { EquipmentType } from '@interfaces/equipment_type';
import type { StructureType } from '@interfaces/structure_type';

export default defineComponent({
  name: 'CurrentBuild',
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true
    }
  },
  setup(props) {
    const i18n = useI18n();
    const namesStore = useLocalizedNamesStore();

    // Load names for domain and faction
    const loadNames = async () => {
      const items: Array<{ type: 'domain' | 'faction'; tech_name?: string; id?: number }> = [];

      if (props.structure.domain) {
        items.push({
          type: 'domain',
          tech_name: props.structure.domain.tech_name || undefined,
          id: props.structure.domain.id || undefined,
        });
      }

      if (props.structure.faction) {
        items.push({
          type: 'faction',
          tech_name: props.structure.faction.tech_name || undefined,
          id: props.structure.faction.id || undefined,
        });
      }

      await namesStore.ensureNamesLoaded(items, i18n.locale.value);
    };

    onMounted(() => {
      loadNames();
    });

    watch(() => i18n.locale.value, () => {
      loadNames();
    });

    watch(() => props.structure, () => {
      loadNames();
    }, { deep: true });

    const domain_name = computed(() => {
      if (!props.structure.domain) return '-';
      const name = namesStore.getName(
        'domain',
        props.structure.domain.tech_name || undefined,
        props.structure.domain.id || undefined
      );
      return name || props.structure.domain.name || '-';
    });

    const faction_name = computed(() => {
      if (!props.structure.faction) return '-';
      const name = namesStore.getName(
        'faction',
        props.structure.faction.tech_name || undefined,
        props.structure.faction.id || undefined
      );
      return name || props.structure.faction.name || '-';
    });

    return {
      t: i18n.t,
      domain_name,
      faction_name
    };
  },
  computed: {
    spellsQuantity() {
      if (this.structure.spells.length == 0) return 0;

      return this.structure.spells.reduce((partialSum, spell) => partialSum + spell.quantity, 0);
    },
    equipQuantity() {
      if (this.structure.equipment.length == 0) return 0;

      return this.structure.equipment.reduce((partialSum, equipment) => partialSum + equipment.quantity, 0);
    }
  },
  methods: {
    addSpell(spell: SpellType) {
      this.$emit("add-spell", spell);
    },
    removeSpell(spell: SpellType) {
      this.$emit("remove-spell", spell);
    },
    addRosterEquipment(equipment: EquipmentType) {
      this.$emit("add-roster-equipment", equipment);
    },
    removeRosterEquipment(equipment: EquipmentType) {
      this.$emit("remove-roster-equipment", equipment);
    },
    showModal(component_name: 'selectSpell' | 'selectEquipment', title?: string) {
      const self = this;

      document.body.classList.add('roster-modal-open');
      $vfm
        .show({
          component: 'CardModal',
          bind: {
            name: 'CardModal',
          },
          slots: {
            title: {
              component: 'VTitle',
              bind: {
                text: title
              }
            },
            default: {
              component: component_name,
              bind: {
                structure: self.structure,
                roster: self.roster,
              },
              on: {
                addRosterSpell(spell: SpellType) {
                  self.addSpell(spell);
                },
                removeRosterSpell(spell: SpellType) {
                  self.removeSpell(spell);
                },
                addRosterEquipment(equip: EquipmentType) {
                  self.addRosterEquipment(equip);
                },
                removeRosterEquipment(equip: EquipmentType) {
                  self.removeRosterEquipment(equip);
                },
              },
            },
          },
          on: {
            confirm(close: () => void) {
              close();
            },
            cancel(close: () => void) {
              close();
            },
          },
        })
        .finally(() => {
          document.body.classList.remove('roster-modal-open');
        });
    }
  }
})
</script>

<template>
  <div class="roster-current">
    <div class="roster-current__main">
      <div class="roster-current__main-fields">
        <roster-name :roster="roster" @changeName="$emit('change-name')"></roster-name>

        <div class="roster-current__points-col">
          <div class="roster-stat roster-stat--spent" :class="{ 'text-danger': (structure?.point_cost ?? 0) > (structure?.max_points ?? 0) }">
            <span class="roster-span">{{ t('current_build.spent') }}&nbsp;</span>
            <span class="roster-number">{{ structure?.point_cost ?? 0 }}</span>
          </div>

          <span class="roster-divider roster-divider--mobile">&nbsp;/&nbsp;</span>

          <roster-format :structure="structure" :roster="roster"></roster-format>

          <div class="roster-stat roster-stat--available" :class="{ 'text-danger': (structure?.point_cost ?? 0) > (structure?.max_points ?? 0) }">
            <span class="roster-span">{{ t('current_build.available') }}&nbsp;</span>
            <span class="roster-number">{{ (structure?.max_points ?? 0) - (structure?.point_cost ?? 0) }}</span>
          </div>
        </div>

        <div class="col"></div>
      </div>

      <div class="row-cards w-mobile-a" v-if='structure.hero != null'>
        <div class="col roster-speleq mr-1">
          <span class="speleq-name">{{ t('current_build.god_cards') }}</span>
          <img class="equipments mobile-none" src="@/images/util/spells.png" @click.prevent="showModal('selectSpell')">
          <span class="speleq-cont" @click.prevent="showModal('selectSpell', t('current_build.spells'))">{{ spellsQuantity }} / 10</span>
          <img class="equipments mobile-image" src="@/images/util/spells_mobile.png" @click.prevent="showModal('selectSpell')">
        </div>
        <div class="col roster-speleq">
          <span class="speleq-name">{{ t('current_build.equipment') }}</span>
          <img class="equipments mobile-none" @click.prevent="showModal('selectEquipment', t('current_build.equipment_title'))"
            src="@/images/util/equip.png">
          <span class="speleq-cont">{{ equipQuantity }}</span>
          <img class="equipments mobile-image" @click.prevent="showModal('selectEquipment', t('current_build.equipment_title'))"
            src="@/images/util/equip_mobile.png">
        </div>
      </div>
    </div>

    <div class="roster-current__side-actions">
      <share-button :roster="roster" :mobile="true"></share-button>
      <redeem-button :roster="roster" :mobile="true"></redeem-button>
    </div>
  </div>
</template>
